// The RFC 9457 problem body's STANDARD MEMBERS, declared once — and the split
// between them and the extension members, which is the same fact read from the
// other end.
//
// It was declared TWICE, in two apps that cannot import each other. `apps/api`
// FLATTENS: `problemResponse` (`apps/api/src/middleware/problem-details.ts`)
// destructures `{extensions, ...base}` and spreads both into one object, so the
// body a client receives has no `extensions` key and carries `retry_after` /
// `request_id` / `required_scope` beside `detail`. `apps/dashboard` SPLITS them
// back apart, and it did so against its own private copy of the member list
// (`lib/problem.ts`: `const PROBLEM_MEMBERS = ['type','title','status','detail',
// 'instance']`). Nothing mechanical held the two ends together: a sixth
// non-extension member on the producer would have been reported to every
// consumer as an extension, and a rename on either side is a silent break in a
// PUBLIC wire contract. `packages/domain` is the only package both apps depend
// on, so it is the only place the line between the two halves can live —
// exactly the `wire/sse-frames.ts` case, for exactly the same reason.
//
// ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────────
//
// The SERIALIZER (`problemResponse` and its thirteen helpers) stays in
// `apps/api`. It takes a Hono `Context` and is a transport adapter, so hosting
// it here would put a server framework behind every workspace that imports
// `domain` — `apps/dashboard`'s browser bundle and `apps/runner` included.
//
// `TYPE_BASE` (`https://developerz.ai/errors/`) stays in `apps/api` too. Unlike
// the member list it is not duplicated: it has ONE reader, the URI minting in
// `problem-details.ts`, and nothing outside that file — no other app, no
// package, not the dashboard — reads it today. A constant moved down a layer
// for a consumer that does not exist is a second place to look for it and
// nothing else; when the dashboard switches on a slug rather than on the whole
// URI, it moves in that change.
//
// This module is deliberately zod-free. The one SCHEMA of a problem body
// (`ProblemBodySchema`) also has a single consumer — `apps/api/src/openapi.ts`,
// which publishes it — and its per-field prose documents that app's own
// extension members. What is shared is the LINE, not the document.

/**
 * The five members RFC 9457 § 3.1 defines. Everything else a problem body
 * carries is an extension member (§ 3.2), which the spec explicitly allows and
 * this platform uses: `request_id` on every refusal, plus `retry_after`,
 * `errors`, `required_scope`, `auth_reason`, `expired_at` and the per-route
 * ones.
 *
 * Order is the RFC's, and it is not load-bearing — {@link problemExtensions}
 * tests membership, never position. It is `as const` so the member names are a
 * type as well as a value.
 */
export const PROBLEM_STANDARD_MEMBERS = ['type', 'title', 'status', 'detail', 'instance'] as const;

/** One of the five. */
export type ProblemStandardMember = (typeof PROBLEM_STANDARD_MEMBERS)[number];

const STANDARD: ReadonlySet<string> = new Set(PROBLEM_STANDARD_MEMBERS);

/**
 * Is this key one of the five, rather than an extension member?
 *
 * The predicate both ends of the wire owe an answer to: the producer decides
 * what to flatten in beside them, the consumer decides what to split back out.
 */
export function isProblemStandardMember(key: string): key is ProblemStandardMember {
  return STANDARD.has(key);
}

/**
 * A problem body's **extension members**, or `undefined` when it has none.
 *
 * The inverse of the producer's flatten, so backpressure arrives as
 * `{detail: 'queue full: 12 tasks waiting (max 12)', retry_after: 30}` —
 * `detail` is the sentence, these are the numbers, and a consumer needing one
 * stops re-parsing the other.
 *
 * A body that isn't a JSON object (HTML, empty, a bare string, an array) yields
 * `undefined`, and so does an object with nothing but standard members: absent
 * and empty are the same answer, and a non-problem error stays exactly the
 * `ApiError` every call site through the one `/v1` client already handles.
 */
export function problemExtensions(body: unknown): Record<string, unknown> | undefined {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return undefined;
  const members = Object.entries(body).filter(([key]) => !isProblemStandardMember(key));
  return members.length > 0 ? Object.fromEntries(members) : undefined;
}
